import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Phone } from 'lucide-react';

import {
  ensureAuthorized,
  getAccessToken,
  openAuthorizePopup,
  signOutRc,
} from '../../../lib/ringcentral/auth';
import { resetWebPhone } from '../../../lib/ringcentral/webphone';
import { useMyRcSmsNumber } from '../../../services/onboardingBoard';
import { formatPhoneToken } from './boardUtils';

// Header RC status (Nic 2026-08-10): texts + calls ride the BROWSER's
// RingCentral login, which used to be invisible until a send failed. The
// chip makes it visible — green with the exact number sends come from,
// red "signed out" that opens the RC sign-in in a POPUP (the page never
// navigates, so open drafts survive).
// apiBase: whose route resolves the texting number (the Sales board's, or
// Client Onboarding's for specialists).
// bare (2026-10-20): no pill of its own, for the Sales header's status pill,
// where it is the first section.
// compact (2026-10-20): the dot and the phone icon only (the number is on
// hover), for a tight Sales Workspace header.
// tiny: signed out shows just the red dot and the phone (still clickable;
// the hover says why), for the narrowest Sales Workspace header.
// The browser's RingCentral login, checked and kept fresh (shared with the
// Sales Workspace's Connections button, 2026-10-20). state: 'checking' |
// 'out' | 'in' | 'no_number'.
export const useRcStatus = (apiBase = '/onboarding-board') => {
  const [state, setState] = useState('checking');
  const [fromNumber, setFromNumber] = useState(null);
  const [signingIn, setSigningIn] = useState(false);
  const resolveNumber = useMyRcSmsNumber({}, apiBase);

  const check = useCallback(async () => {
    const authorized = await ensureAuthorized();
    if (!authorized) {
      setState('out');
      setFromNumber(null);
      return;
    }
    try {
      const data = await resolveNumber.mutateAsync({
        rcAccessToken: getAccessToken(),
      });
      if (data?.fromNumber) {
        setFromNumber(data.fromNumber);
        setState('in');
      } else {
        setFromNumber(null);
        setState('no_number');
      }
    } catch (err) {
      setFromNumber(null);
      // A token RingCentral itself rejected = signed OUT (red chip, click
      // to re-login) — never the amber "no SMS number" misread
      // (2026-08-11: a stale browser token wore amber for hours).
      if (err?.response?.data?.code === 'rc_login_required') {
        setState('out');
        return;
      }
      // A genuine RC hiccup: show signed-in without a number rather than
      // lying either way.
      setState('no_number');
    }
    // resolveNumber is a react-query mutation object; its identity churns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    check();
    // Re-check when the tab returns and when another tab/popup writes the
    // RC tokens (localStorage is shared per origin).
    const onFocus = () => check();
    const onStorage = (e) => {
      if (!e.key || e.key === 'rc_tokens') check();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
    };
  }, [check]);

  const signIn = async () => {
    if (signingIn) return;
    setSigningIn(true);
    try {
      await openAuthorizePopup();
    } finally {
      setSigningIn(false);
      check();
    }
  };
  // Sign out of RingCentral here (Nic 2026-11-02): the phone app's token
  // only picks up a new scope on a fresh login, and until now the only way
  // was to clear the browser. Drops the tokens and the phone registration;
  // the chip turns red with Sign in.
  const signOut = () => {
    signOutRc();
    try {
      resetWebPhone();
    } catch (_) {}
    setFromNumber(null);
    setState('out');
  };
  return { state, fromNumber, signingIn, signIn, signOut };
};

const RcStatusChip = ({
  apiBase = '/onboarding-board',
  bare = false,
  compact = false,
  tiny = false,
} = {}) => {
  const { state, fromNumber, signingIn, signIn } = useRcStatus(apiBase);

  const base = bare
    ? 'flex items-center gap-1.5 whitespace-nowrap text-[11px] font-medium'
    : 'flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[11px] font-medium shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10';

  if (state === 'checking') {
    return (
      <span className={`${base} text-slate-400 dark:text-slate-500`}>
        <span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-slate-600" />
        {compact ? null : 'RingCentral'}
      </span>
    );
  }

  if (state === 'out') {
    // The ONE action-needed state on the row. An OUTLINE (Nic 2026-10-06):
    // red ring and text with a pulsing dot, so it stands apart from the
    // filled controls around it instead of being one more filled pill.
    return (
      <button
        type="button"
        onClick={signIn}
        title="Not signed into RingCentral in this browser, and texts and calls need it. Opens the RingCentral sign-in."
        className={
          bare
            ? 'flex items-center gap-2 whitespace-nowrap rounded-full bg-transparent text-[11px] font-semibold text-red-600 hover:underline dark:!bg-transparent dark:!text-red-400'
            : 'flex items-center gap-2 rounded-full bg-transparent px-4 py-1.5 text-[11px] font-semibold text-red-600 ring-[1.5px] ring-inset ring-red-500 hover:bg-red-50 dark:!bg-transparent dark:!text-red-400 dark:ring-red-400 dark:hover:!bg-red-500/10'
        }
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
        </span>
        <Phone size={11} />
        {/* Compact never grows while signing in (Nic 2026-10-20: "Signing
            in..." pushed past the bar): a spinner stands in for the words. */}
        {compact || tiny ? (
          signingIn ? (
            <Loader2 size={11} className="animate-spin" />
          ) : tiny ? null : (
            'Sign in'
          )
        ) : signingIn ? (
          'Signing in...'
        ) : (
          'Sign into RingCentral'
        )}
      </button>
    );
  }

  if (state === 'no_number') {
    return (
      <span
        className={`${base} !bg-amber-50 text-amber-700 dark:!bg-amber-500/15 dark:text-amber-400`}
        title="Signed into RingCentral, but no SMS-enabled number was found on this login."
      >
        <span className="h-2 w-2 rounded-full bg-amber-500" />
        {compact ? 'No SMS' : 'RC: no SMS number'}
      </span>
    );
  }

  return (
    <span
      className={`${base} text-slate-600 dark:text-slate-300`}
      title={`Signed into RingCentral. Your texts and calls use ${formatPhoneToken(fromNumber) || 'this line'}.`}
    >
      <span className="h-2 w-2 rounded-full bg-emerald-500" />
      <Phone size={11} className="text-slate-400 dark:text-slate-500" />
      {compact ? null : formatPhoneToken(fromNumber)}
    </span>
  );
};

export default RcStatusChip;
